import { ReadMoreHtml } from "@/components/read-more/ReadMoreHtml";
import {
  shouldIncludeAttachment,
  useGetAttachmentsByDocId,
  type Attachment as ApiAttachment,
} from "@/api/attachment";
import { ImageFullscreen } from "@/components/modal/imageFullscreen";
import { Accordion } from "@/components/ui/accordion";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import { useAuth } from "@/context/auth/AuthContext";
import { SupportedLocale } from "@/i18n";
import { filterPublishedMultiplesampleAuctions } from "@/lib/multiplesample-auctions";
import {
  DimensionsFormatting,
  hasDisplayableDimensions,
} from "@/lib/compute-field/shared/dimensions";
import { SupportTechniqueCF } from "@/lib/compute-field/shared/technique";
import {
  computeFieldCollectorNameForOverview,
  computeFieldRelatedAuctions,
  computeFieldRelatedBibliographiesMultiplesamples,
  computeFieldRelatedExhibitions,
} from "@/lib/computeFields";
import { getDefaultAttachment } from "@/lib/image";
import { DimensionsDTO } from "@/lib/shared-types";
import { Multipleprint, PublicationStatus } from "@/lib/types";
import { formatTextWithColon, parseJsonOrUndefined } from "@/lib/utils";
import { AuctionsAccordion } from "@/pages/dashboard/artwork-overview/accordions/AuctionsAccordion";
import { HistoryAccordion } from "@/pages/dashboard/artwork-overview/accordions/HistoryAccordion";
import { useMemo, useState } from "react";
import { BibliographyAccordion } from "./BibliographyAccordion";
import { ExhibitionsAccordion } from "./ExhibitionsAccordion";
import { buildMultiplesampleProvenanceHistory } from "./provenanceTimeline";

/** Exemplaire with optional relations returned by API (collector, history, multipleSampleInAuction, dimensions, etc.) */
interface ExemplaireWithRelations {
  id: string;
  instanceId: string;
  exemplaireDansEstampesMultiplesDescriptionNumeroEnregistrement?: string;
  exemplaireDansEstampesMultiplesDescriptionNumero: string;
  exemplaireDansEstampesMultiplesDescriptionNumeroInventaire: string;
  exemplaireDansEstampesMultiplesDescriptionNumeroInventairePubli?: boolean;
  exemplaireDansEstampesMultiplesDescriptionLocalisation: string;
  exemplaireDansEstampesMultiplesDescriptionNotes?: string;
  exemplaireDansEstampesMultiplesDescriptionStatus?: string;
  exemplaireDansEstampesMultiplesDescriptionDateTirage?: string;
  exemplaireDansEstampesMultiplesDescriptionType?: string;
  exemplaireDansEstampesMultiplesDescriptionPrinter?: string;
  exemplaireDansEstampesMultiplesDescriptionEditor?: string;
  exemplaireDansEstampesDimensions?: string | DimensionsDTO;
  exemplaireDansEstampesDimensionsImage?: string | DimensionsDTO;
  exemplaireDansEstampesMultiplesTechnique?: Record<string, string>;
  exemplaireDansEstampesMultiplesSupport?: Record<string, string>;
  exemplaireDansEstampesMultiplesEditionNumber?: Record<string, string>;
  exemplaireDansEstampesMultiplesSignatureInscription?: Record<string, string>;
  exemplaireDansEstampesMultiplesRemarquesPubliquesMultilingual?: Record<
    string,
    string
  >;
  exemplaireDansEstampesMultiplesRemarquesPubliquesPublished?: boolean;
  exemplaireDansEstampesMultiplesRemarquesPubliquesLabelPubli?: boolean;
  multipleprints?: Multipleprint[];
  collector?: {
    id: string;
    collectionneursDescriptionNom?: string;
    collectionneursDescriptionPrenom?: string;
    collectionneursDescriptionCollectionPublique?: boolean;
    collectionneursDescriptionVille?: string;
    collectionneursDescriptionVillePublished?: boolean;
    collectionneursDescriptionPays?: string;
    collectionneursDescriptionPaysPublished?: boolean;
  };
  history?: Array<{
    id?: string;
    id_imported?: number;
    provenanceRank?: number;
    exemplaireDansEstampesMultiplesHistoryDescriptionCollectionPart?: boolean;
    exemplaireDansEstampesMultiplesHistoryDescriptionNom?: string;
    exemplaireDansEstampesMultiplesHistoryDescriptionVille?: string;
    exemplaireDansEstampesMultiplesHistoryDescriptionDates?: string;
  }>;
  multipleSampleInAuction?: Array<{
    id?: string;
    provenanceRank?: number;
    auction?: {
      ventesDescriptionNumeroVente?: string;
      ventesDescriptionTitre?: string;
      ventesDescriptionDate?: string;
      ventesDescriptionLieu?: string;
      ventesDescriptionVille?: string;
      ventesDescriptionPays?: string;
      ventesDescriptionResume?: string;
      ventesPublicationStatus?: string;
    };
    multiplesampleDansVentesDescriptionNumeroLot?: string;
    multiplesampleDansVentesDescriptionReproduitPage?: string;
    multiplesampleDansVentesDescriptionNonReproduit?: boolean;
  }>;
  multipleSampleInExhibitions?: Array<{
    exhibition?: { expositionsDescriptionResume?: string };
    multiplesampleDansExpositionsDescriptionNumeroCatalogue?: string;
    multiplesampleDansExpositionsNotes?: string;
  }>;
  multipleSampleInBibliography?: Array<{
    bibliography?: { bibliographieDescriptionResumeLong?: string };
    multiplesampleDansBibliographieDescriptionReproduitPage?: string;
    multiplesampleDansBibliographieDescriptionReproduitPlanche?: string;
    multiplesampleDansBibliographieDescriptionPlancheNonNumerotee?: boolean;
    multiplesampleDansBibliographieDescriptionCitePage?: string;
    multiplesampleDansBibliographieDescriptionListePage?: string;
    multiplesampleDansBibliographieDescriptionSousNumero?: string;
    multiplesampleDansBibliographieDescriptionPhotographe?: string;
    multiplesampleDansBibliographieDescriptionReproductionCouleur?: boolean;
    multiSampleInBiblioDescReproCover?: boolean;
    multiplesampleDansBibliographieDescriptionNonPagine?: boolean;
    multiplesampleDansBibliographieDescriptionTexteLibre?: string;
  }>;
}

interface ExemplaireDrawerProps {
  exemplaire: ExemplaireWithRelations;
  multipleprint: Multipleprint;
  locale: SupportedLocale;
  t: (key: string) => string;
  trigger: React.ReactNode;
  /** Attachments already loaded by the accordion row/parent (avoids remount fetches). */
  preloadedExemplaireAttachments?: ApiAttachment[];
  preloadedMultipleprintAttachments?: ApiAttachment[];
}

function getMultilingualField(
  field: Record<string, string> | undefined,
  locale: string,
): string | null {
  if (!field || typeof field !== "object") return null;
  return field[locale] ?? Object.values(field)[0] ?? null;
}

function getAttachmentUrl(attachment: {
  mediumPublicUrl?: string;
  largePublicUrl?: string;
  publicUrl?: string;
}): string | null {
  return (
    attachment.mediumPublicUrl ??
    attachment.largePublicUrl ??
    attachment.publicUrl ??
    null
  );
}

export function ExemplaireDrawer({
  exemplaire,
  multipleprint,
  locale,
  t,
  trigger,
  preloadedExemplaireAttachments,
  preloadedMultipleprintAttachments,
}: ExemplaireDrawerProps) {
  const { instanceData } = useAuth();
  const provenanceMode = instanceData?.provenanceMode ?? false;
  // Gate fetches on sheet open — mounting every row used to fire N×2 attachment queries.
  const [open, setOpen] = useState(false);

  const publishedSampleAuctions = useMemo(
    () => filterPublishedMultiplesampleAuctions(exemplaire.multipleSampleInAuction),
    [exemplaire.multipleSampleInAuction],
  );
  const publishedParentAuctions = useMemo(
    () =>
      (multipleprint.multipleprintInAuction ?? []).filter(
        (item) => item.auction?.ventesPublicationStatus === "published",
      ),
    [multipleprint.multipleprintInAuction],
  );

  const listExemplaireAttachments =
    preloadedExemplaireAttachments ??
    ((exemplaire as ExemplaireWithRelations & { attachments?: ApiAttachment[] })
      .attachments as ApiAttachment[] | undefined);
  const listMultipleprintAttachments =
    preloadedMultipleprintAttachments ??
    (multipleprint.attachments as ApiAttachment[] | undefined);

  const { data: fetchedExemplaireAttachments } = useGetAttachmentsByDocId(
    open && !listExemplaireAttachments?.length ? exemplaire.id : undefined,
  );
  const { data: fetchedMultipleprintAttachments } = useGetAttachmentsByDocId(
    open && !listMultipleprintAttachments?.length
      ? multipleprint.id
      : undefined,
  );

  const exemplaireAttachments = listExemplaireAttachments?.length
    ? listExemplaireAttachments
    : fetchedExemplaireAttachments;
  const multipleprintAttachments = listMultipleprintAttachments?.length
    ? listMultipleprintAttachments
    : fetchedMultipleprintAttachments;

  const exemplairePublicationStatus = (exemplaire.exemplaireDansEstampesMultiplesDescriptionStatus ??
    "draft") as PublicationStatus;
  const multipleprintPublicationStatus = (multipleprint.estampesMultiplesPublicationStatus ??
    "draft") as PublicationStatus;

  const filteredExemplaireAttachments = useMemo(
    () =>
      exemplaireAttachments?.filter((a) =>
        shouldIncludeAttachment(a, exemplairePublicationStatus),
      ) ?? [],
    [exemplaireAttachments, exemplairePublicationStatus],
  );
  const filteredMultipleprintAttachments = useMemo(
    () =>
      multipleprintAttachments?.filter((a) =>
        shouldIncludeAttachment(a, multipleprintPublicationStatus),
      ) ?? [],
    [multipleprintAttachments, multipleprintPublicationStatus],
  );

  const attachments = filteredExemplaireAttachments.length
    ? filteredExemplaireAttachments
    : filteredMultipleprintAttachments;

  const defaultImage = useMemo(() => {
    if (!attachments.length) return null;
    const attachment = filteredExemplaireAttachments.length
      ? getDefaultAttachment(filteredExemplaireAttachments, "multiplesample")
      : getDefaultAttachment(attachments, "multipleprint");
    return attachment ? getAttachmentUrl(attachment) : null;
  }, [attachments, filteredExemplaireAttachments]);

  const dimensionsWithMargins = useMemo(() => {
    if (!exemplaire?.exemplaireDansEstampesDimensions) return null;
    return typeof exemplaire.exemplaireDansEstampesDimensions === "string"
      ? parseJsonOrUndefined<DimensionsDTO>(
        exemplaire.exemplaireDansEstampesDimensions,
      )
      : (exemplaire.exemplaireDansEstampesDimensions as DimensionsDTO);
  }, [exemplaire?.exemplaireDansEstampesDimensions]);

  const dimensionsWithoutMargins = useMemo(() => {
    if (!exemplaire?.exemplaireDansEstampesDimensionsImage) return null;
    return typeof exemplaire.exemplaireDansEstampesDimensionsImage === "string"
      ? parseJsonOrUndefined<DimensionsDTO>(
        exemplaire.exemplaireDansEstampesDimensionsImage,
      )
      : (exemplaire.exemplaireDansEstampesDimensionsImage as DimensionsDTO);
  }, [exemplaire?.exemplaireDansEstampesDimensionsImage]);

  // Des dimensions stockees mais vides (objet JSON sans aucune mesure) rendaient
  // quand meme le libelle « Dim. image : » suivi de rien (ticket #1055).
  const hasDimensionsWithMargins =
    hasDisplayableDimensions(dimensionsWithMargins);
  const hasDimensionsWithoutMargins = hasDisplayableDimensions(
    dimensionsWithoutMargins,
  );

  const exhibitionsCalculated = useMemo(() => {
    const items = exemplaire.multipleSampleInExhibitions;
    if (!items?.length) return "";
    const adapted = items
      .filter((m) => !!m.exhibition)
      .map((m) => ({
        exhibition: m.exhibition!,
        oeuvresDansExpositionsDescriptionNumeroCatalogue:
          m.multiplesampleDansExpositionsDescriptionNumeroCatalogue ?? "",
      }));
    return computeFieldRelatedExhibitions(
      {
        relatedExhbitions: adapted as Parameters<
          typeof computeFieldRelatedExhibitions
        >[0]["relatedExhbitions"],
      },
      { language: locale, translation: t },
    );
  }, [exemplaire.multipleSampleInExhibitions, locale, t]);

  const bibliographyCalculated = useMemo(() => {
    const items = exemplaire.multipleSampleInBibliography;
    if (!items?.length) return "";
    const adapted = items.filter((m) => !!m.bibliography).map((m) => ({
      bibliography: m.bibliography!,
      multiplesampleDansBibliographieDescriptionCitePage:
        m.multiplesampleDansBibliographieDescriptionCitePage ?? "",
      multiplesampleDansBibliographieDescriptionReproduitPage:
        m.multiplesampleDansBibliographieDescriptionReproduitPage ?? "",
      multiplesampleDansBibliographieDescriptionReproduitPlanche:
        m.multiplesampleDansBibliographieDescriptionReproduitPlanche ?? "",
      multiplesampleDansBibliographieDescriptionPlancheNonNumerotee:
        m.multiplesampleDansBibliographieDescriptionPlancheNonNumerotee ?? false,
      multiplesampleDansBibliographieDescriptionListePage:
        m.multiplesampleDansBibliographieDescriptionListePage ?? "",
      multiplesampleDansBibliographieDescriptionSousNumero:
        m.multiplesampleDansBibliographieDescriptionSousNumero ?? "",
      multiplesampleDansBibliographieDescriptionPhotographe:
        m.multiplesampleDansBibliographieDescriptionPhotographe ?? "",
      multiplesampleDansBibliographieDescriptionReproductionCouleur:
        m.multiplesampleDansBibliographieDescriptionReproductionCouleur ?? false,
      multiplesampleDansBibliographieDescriptionNonPagine:
        m.multiplesampleDansBibliographieDescriptionNonPagine ?? false,
      multiSampleInBiblioDescReproCover:
        m.multiSampleInBiblioDescReproCover ?? false,
      multiplesampleDansBibliographieDescriptionTexteLibre:
        m.multiplesampleDansBibliographieDescriptionTexteLibre ?? "",
    }));
    return computeFieldRelatedBibliographiesMultiplesamples(
      {
        relatedBibliographies: adapted as Parameters<
          typeof computeFieldRelatedBibliographiesMultiplesamples
        >[0]["relatedBibliographies"],
      },
      { language: locale, translation: t },
    );
  }, [exemplaire.multipleSampleInBibliography, locale, t]);

  const exemplaireAuctionCalculated = useMemo(() => {
    if (!publishedSampleAuctions.length) return null;
    const adapted = publishedSampleAuctions
      .filter((m) => !!m.auction)
      .map((m) => ({
        auction: m.auction!,
        oeuvresDansVentesDescriptionNumeroLot:
          m.multiplesampleDansVentesDescriptionNumeroLot ?? "",
        oeuvresDansVentesDescriptionReproduitPage:
          m.multiplesampleDansVentesDescriptionReproduitPage ?? "",
        oeuvresDansVentesDescriptionNonReproduit:
          m.multiplesampleDansVentesDescriptionNonReproduit ?? false,
      }));
    return computeFieldRelatedAuctions(
      {
        relatedAuctions: adapted as Parameters<
          typeof computeFieldRelatedAuctions
        >[0]["relatedAuctions"],
      },
      { language: locale, translation: t },
    );
  }, [publishedSampleAuctions, locale, t]);

  const parentAuctionCalculated = useMemo(() => {
    if (!publishedParentAuctions.length) return null;
    return computeFieldRelatedAuctions(
      {
        relatedAuctions: publishedParentAuctions.map((item) => ({
          auction: item.auction,
          oeuvresDansVentesDescriptionNumeroLot:
            item.ventesDansEstampesMultiplesDescriptionNumeroLot,
          oeuvresDansVentesDescriptionReproduitPage:
            item.ventesDansEstampesMultiplesDescriptionReproduitPage,
          oeuvresDansVentesDescriptionNonReproduit: false,
        })),
      },
      { language: locale, translation: t },
    );
  }, [publishedParentAuctions, locale, t]);

  const auctionCalculated =
    exemplaireAuctionCalculated ?? parentAuctionCalculated;

  const historyCalculated = useMemo(() => {
    return buildMultiplesampleProvenanceHistory({
      history: exemplaire.history,
      auctions: provenanceMode ? publishedSampleAuctions : [],
      t,
    });
  }, [exemplaire.history, provenanceMode, publishedSampleAuctions, t]);

  const title =
    exemplaire.exemplaireDansEstampesMultiplesDescriptionNumeroEnregistrement ||
    exemplaire.exemplaireDansEstampesMultiplesDescriptionNumero ||
    exemplaire.exemplaireDansEstampesMultiplesDescriptionNumeroInventaire ||
    t("singleMultipleprintSubtitleExemplaire");

  const hasAccordions =
    (exemplaire.history && exemplaire.history.length > 0) ||
    (exemplaire.multipleSampleInExhibitions &&
      exemplaire.multipleSampleInExhibitions.length > 0) ||
    (exemplaire.multipleSampleInBibliography &&
      exemplaire.multipleSampleInBibliography.length > 0) ||
    publishedSampleAuctions.length > 0 ||
    publishedParentAuctions.length > 0;

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger asChild>{trigger}</SheetTrigger>
      <SheetContent
        side="right"
        className="w-full sm:max-w-2xl overflow-y-auto p-6"
      >
        <SheetHeader>
          <SheetTitle className="text-center">{title}</SheetTitle>
        </SheetHeader>
        <div className="flex flex-col items-center gap-4 mt-6">
          {!exemplaire && (
            <p className="font-semibold text-sm text-center w-full">
              Chargement...
            </p>
          )}

          {exemplaire && (
            <>
              {defaultImage && attachments && attachments.length > 0 && (
                <div className="flex w-full max-w-md justify-center self-center">
                  <ImageFullscreen
                    media={{
                      attachments,
                      type: exemplaireAttachments?.length
                        ? "multiplesample"
                        : "multipleprint",
                      alt: title,
                      width: 600,
                      height: 600,
                      className:
                        "block object-contain object-center max-h-[400px] w-auto max-w-full",
                    }}
                  />
                </div>
              )}

              {(multipleprint.estampesMultiplesDescriptionTitre ||
                multipleprint.estampesMultiplesDescriptionDateDeCreation) && (
                <p className="font-bold text-[15px] text-center w-full">
                  {multipleprint.estampesMultiplesDescriptionTitre}
                  {multipleprint.estampesMultiplesDescriptionTitre &&
                    multipleprint.estampesMultiplesDescriptionDateDeCreation &&
                    " – "}
                  {multipleprint.estampesMultiplesDescriptionDateDeCreation}
                </p>
              )}

              {exemplaire.exemplaireDansEstampesMultiplesDescriptionNumero && (
                <p className="font-normal text-sm text-center w-full">
                  {formatTextWithColon(
                    t("singleMultipleprintExemplaireNumero"),
                    locale,
                    exemplaire.exemplaireDansEstampesMultiplesDescriptionNumero,
                  )}
                </p>
              )}

              {exemplaire.exemplaireDansEstampesMultiplesDescriptionNumeroEnregistrement && (
                <p className="font-normal text-sm text-center w-full">
                  {formatTextWithColon(
                    t("singleMultipleprintFullscreenNumeroEnregistrement"),
                    locale,
                    exemplaire.exemplaireDansEstampesMultiplesDescriptionNumeroEnregistrement,
                  )}
                </p>
              )}

              {exemplaire.exemplaireDansEstampesMultiplesDescriptionNumeroInventaire &&
                exemplaire.exemplaireDansEstampesMultiplesDescriptionNumeroInventairePubli !==
                  false && (
                  <p className="font-normal text-sm text-center w-full">
                    {formatTextWithColon(
                      t("singleMultipleprintExemplaireNumeroInventaire"),
                      locale,
                      exemplaire.exemplaireDansEstampesMultiplesDescriptionNumeroInventaire,
                    )}
                  </p>
                )}

              {exemplaire.exemplaireDansEstampesMultiplesDescriptionDateTirage && (
                <p className="font-normal text-sm text-center w-full">
                  {formatTextWithColon(
                    t("singleMultipleprintExemplaireDateTirage"),
                    locale,
                    exemplaire.exemplaireDansEstampesMultiplesDescriptionDateTirage,
                  )}
                </p>
              )}

              {exemplaire.exemplaireDansEstampesMultiplesDescriptionType && (
                <p className="font-normal text-sm text-center w-full">
                  {formatTextWithColon(
                    t("singleMultipleprintExemplaireType"),
                    locale,
                    exemplaire.exemplaireDansEstampesMultiplesDescriptionType,
                  )}
                </p>
              )}

              {exemplaire.exemplaireDansEstampesMultiplesDescriptionPrinter && (
                <p className="font-normal text-sm text-center w-full">
                  {formatTextWithColon(
                    t("singleMultipleprintFullscreenTirageRealisePar"),
                    locale,
                    exemplaire.exemplaireDansEstampesMultiplesDescriptionPrinter,
                  )}
                </p>
              )}

              {exemplaire.exemplaireDansEstampesMultiplesDescriptionEditor && (
                <p className="font-normal text-sm text-center w-full">
                  {formatTextWithColon(
                    t("exemplaireDansEstampesMultiplesDescriptionEditor"),
                    locale,
                    exemplaire.exemplaireDansEstampesMultiplesDescriptionEditor,
                  )}
                </p>
              )}

              {(exemplaire.exemplaireDansEstampesMultiplesTechnique ||
                exemplaire.exemplaireDansEstampesMultiplesSupport) && (
                <p className="font-normal text-sm text-center w-full">
                  {formatTextWithColon(
                    t("singleMultipleprintExemplaireTechniqueSupport"),
                    locale,
                    "",
                  )}
                  <SupportTechniqueCF
                    input={{
                      technique:
                        exemplaire.exemplaireDansEstampesMultiplesTechnique ??
                        undefined,
                      support:
                        exemplaire.exemplaireDansEstampesMultiplesSupport ??
                        undefined,
                      preposition: t(
                        "computeField.techniqueSupportPreposition",
                      ),
                    }}
                    language={locale}
                    lineBreak={false}
                  />
                </p>
              )}

              {getMultilingualField(
                exemplaire.exemplaireDansEstampesMultiplesEditionNumber,
                locale,
              ) && (
                <p className="font-normal text-sm text-center w-full">
                  {formatTextWithColon(
                    t("singleMultipleprintExemplaireEditionNumber"),
                    locale,
                    getMultilingualField(
                      exemplaire.exemplaireDansEstampesMultiplesEditionNumber,
                      locale,
                    ) ?? "",
                  )}
                </p>
              )}

              {getMultilingualField(
                exemplaire.exemplaireDansEstampesMultiplesSignatureInscription,
                locale,
              ) && (
                <p className="font-normal text-sm text-center w-full">
                  {formatTextWithColon(
                    t("singleMultipleprintFullscreenSignature"),
                    locale,
                    getMultilingualField(
                      exemplaire.exemplaireDansEstampesMultiplesSignatureInscription,
                      locale,
                    ) ?? "",
                  )}
                </p>
              )}

              {hasDimensionsWithMargins && dimensionsWithMargins && (
                <p className="font-normal text-sm text-center w-full">
                  {formatTextWithColon(
                    t(
                      hasDimensionsWithoutMargins
                        ? "singleMultipleprintExemplaireDimensionsSheet"
                        : "singleMultipleprintExemplaireDimensions",
                    ),
                    locale,
                    "",
                  )}
                  <DimensionsFormatting
                    input={dimensionsWithMargins}
                    language={locale}
                    lineBreak={false}
                  />
                </p>
              )}

              {hasDimensionsWithoutMargins && dimensionsWithoutMargins && (
                <p className="font-normal text-sm text-center w-full">
                  {formatTextWithColon(
                    t("singleMultipleprintExemplaireDimensionsImage"),
                    locale,
                    "",
                  )}
                  <DimensionsFormatting
                    input={dimensionsWithoutMargins}
                    language={locale}
                    lineBreak={false}
                  />
                </p>
              )}

              {exemplaire.collector &&
                typeof exemplaire.collector === "object" && (
                  <p className="font-normal text-sm text-center w-full">
                    {formatTextWithColon(
                      t("singleMultipleprintExemplaireCollector"),
                      locale,
                      "",
                    )}
                    {computeFieldCollectorNameForOverview(
                      {
                        collectionneursDescriptionNom:
                          exemplaire.collector.collectionneursDescriptionNom ??
                          "",
                        collectionneursDescriptionPrenom:
                          exemplaire.collector
                            .collectionneursDescriptionPrenom ?? "",
                        collectionneursDescriptionCollectionPublique:
                          exemplaire.collector
                            .collectionneursDescriptionCollectionPublique ??
                          false,
                        collectionneursDescriptionVille:
                          exemplaire.collector.collectionneursDescriptionVille,
                        collectionneursDescriptionVillePublished:
                          exemplaire.collector.collectionneursDescriptionVillePublished,
                        collectionneursDescriptionPays:
                          exemplaire.collector.collectionneursDescriptionPays,
                        collectionneursDescriptionPaysPublished:
                          exemplaire.collector.collectionneursDescriptionPaysPublished,
                        showCity: true,
                      },
                      { language: locale, translation: t },
                    )}
                  </p>
                )}

              {multipleprint.estampesMultiplesPhotosCreditOeuvres && (
                <p className="font-normal text-sm text-center w-full">
                  {formatTextWithColon(
                    t("singleMultipleprintDescriptionCreditLabel"),
                    locale,
                    multipleprint.estampesMultiplesPhotosCreditOeuvres,
                  )}
                </p>
              )}

              {exemplaire.exemplaireDansEstampesMultiplesRemarquesPubliquesPublished ===
                true &&
                exemplaire
                  .exemplaireDansEstampesMultiplesRemarquesPubliquesMultilingual?.[
                  locale
                ] && (
                  <div className="my-4 flex flex-col gap-2 font-medium text-sm text-center w-full">
                    {exemplaire.exemplaireDansEstampesMultiplesRemarquesPubliquesLabelPubli !==
                      false && t("singleArtworkSubtitlePublicNote")}
                    <ReadMoreHtml
                      className="font-normal text-sm"
                      locale={locale}
                      html={
                        exemplaire
                          .exemplaireDansEstampesMultiplesRemarquesPubliquesMultilingual[
                          locale
                        ]
                      }
                    />
                  </div>
                )}

              {hasAccordions && (
                <div className="w-3/4 mt-6">
                  <Accordion
                    type="multiple"
                    className="font-normal text-base text-gray-800"
                  >
                    {historyCalculated ? (
                      <HistoryAccordion
                        historyCalculated={historyCalculated}
                        tirageHistoryCalculated={null}
                        t={t}
                        title={t("exemplaireTabProvenance")}
                      />
                    ) : null}
                    {(exemplaire.multipleSampleInExhibitions?.length ?? 0) >
                      0 &&
                      exhibitionsCalculated && (
                        <ExhibitionsAccordion
                          exhibitionsCalculated={exhibitionsCalculated}
                          t={t}
                        />
                      )}
                    {(exemplaire.multipleSampleInBibliography?.length ?? 0) >
                      0 &&
                      bibliographyCalculated && (
                        <BibliographyAccordion
                          bibliographyCalculated={bibliographyCalculated}
                          t={t}
                        />
                      )}
                    {!provenanceMode && auctionCalculated && (
                      <AuctionsAccordion
                        auctionCalculated={auctionCalculated}
                        t={t}
                      />
                    )}
                  </Accordion>
                </div>
              )}
            </>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}
